// Response-window options, folded so a big hand does not turn into a wall of buttons.
// Abilities that use a hand card ("play card X", "discard any card to …") are one button each;
// choosing it opens a play area and the player picks the card from the hand — the same
// place / take-back interaction as adding cards to a check. Perfect Number's eight faces are
// one row of numbers. Every legal answer stays reachable; only the presentation changes.
import * as uf from "../../../../common/unfathomable";
import { CardFace } from "./CardFace";
import { Rich } from "./RichText";
import css from "./Unfathomable.module.css";

/** Options whose id is `prefix:<card uid>[:variant]` and that play that very card. */
const kPlayCard = ["ki", "ct", "nth", "fl", "prep", "det", "shrug"];
/** Options whose id is `prefix:<card uid>` and that discard / add any card as a cost. */
const kCostCard = ["almanac", "fortunate", "experienced", "healing"];

/** Card labels look like "协同努力(影响1)". */
const kCardLabel = /[^\s：，、(（]+\((?:影响|学识|观察|力量|意志|背叛)\d+\)/;

export interface CardGroup { kind: "cards"; key: string; label: string; items: { id: string; uid: number }[] }
interface NumberGroup { kind: "number"; key: string; label: string; items: { id: string; n: number }[] }
interface Plain { kind: "plain"; key: string; option: uf.Option }
type Group = CardGroup | NumberGroup | Plain;

export function groupResponseOptions(options: uf.Option[], hand: uf.SkillCard[]): Group[] {
  const out: Group[] = [];
  const byKey = new Map<string, Group>();
  const nameOf = (uid: number) => { const c = hand.find(x => x.uid === uid); return c ? uf.skillDef(c.def).name : null; };
  for (const o of options) {
    const m = o.id.match(/^([a-zA-Z]+):(\d+)(?::(.+))?$/);
    if (m && m[1] === "perfectNumber") {
      let g = byKey.get("pn") as NumberGroup | undefined;
      if (!g) { g = { kind: "number", key: "pn", label: "完美数字：把骰子设为", items: [] }; byKey.set("pn", g); out.push(g); }
      g.items.push({ id: o.id, n: +m[2] });
      continue;
    }
    if (m && (kPlayCard.includes(m[1]) || kCostCard.includes(m[1]))) {
      // One group per ability (and variant, e.g. +2 / −2); the card itself is picked from the hand.
      const key = `${m[1]}:${m[3] ?? ""}`;
      let g = byKey.get(key) as CardGroup | undefined;
      if (!g) {
        const play = kPlayCard.includes(m[1]);
        const name = nameOf(+m[2]);
        const label = play ? o.label.replace(kCardLabel, name ?? "这张牌") : o.label.replace(kCardLabel, "1 张手牌");
        g = { kind: "cards", key, label, items: [] };
        byKey.set(key, g); out.push(g);
      }
      g.items.push({ id: o.id, uid: +m[2] });
      continue;
    }
    out.push({ kind: "plain", key: o.id, option: o });
  }
  return out;
}

/** Hand card → option id for the ability being used (drives the hand's clickable cards). */
export function cardsOfGroup(options: uf.Option[], hand: uf.SkillCard[], key: string | null): Map<number, string> {
  const m = new Map<number, string>();
  if (!key) return m;
  const g = groupResponseOptions(options, hand).find(x => x.key === key);
  if (g?.kind === "cards") for (const it of g.items) m.set(it.uid, it.id);
  return m;
}

export function ResponseOptions({ options, hand, artOk, active, setActive, selected, setSelected, onAnswer }: {
  options: uf.Option[];
  hand: uf.SkillCard[];
  artOk: boolean;
  /** The card ability being used (its play area is open). */
  active: string | null;
  setActive: (key: string | null) => void;
  /** The option id of the card placed in the play area (at most one). */
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  onAnswer: (choice: string[]) => void;
}) {
  const groups = groupResponseOptions(options, hand);
  return (
    <div className={css.options}>
      {groups.map(g => {
        if (g.kind === "plain") {
          return (
            <button key={g.key} className={`${css.btn} ${g.option.id === "pass" ? css.btnGhost : ""}`} onClick={() => onAnswer([g.option.id])}>
              <Rich text={g.option.label} />
            </button>
          );
        }
        if (g.kind === "number") {
          return (
            <div key={g.key} className={css.respRow}>
              <div className={css.respRowLabel}>{g.label}</div>
              <div className={css.respNumbers}>
                {g.items.slice().sort((a, b) => a.n - b.n).map(it => (
                  <button key={it.id} className={`${css.btn} ${css.btnSmall}`} onClick={() => onAnswer([it.id])}>{it.n}</button>
                ))}
              </div>
            </div>
          );
        }
        const on = active === g.key;
        const staged = on ? g.items.find(it => selected.includes(it.id)) : undefined;
        const card = staged ? hand.find(c => c.uid === staged.uid) : undefined;
        return (
          <div key={g.key} className={`${css.respRow} ${on ? css.respActive : ""}`}>
            <button className={css.btn} onClick={() => { setSelected([]); setActive(on ? null : g.key); }}>
              <Rich text={g.label} /><span className={css.respCount}>{g.items.length} 张可用 {on ? "▴" : "▾"}</span>
            </button>
            {on && (
              <>
                <div className={css.playArea}>
                  {card ? (
                    <CardFace key={staged!.id} kind="skill" id={card.def} artOk={artOk} width={74} layoutId={`uf-card-${card.uid}`}
                      onClick={() => setSelected([])} style={{ cursor: "pointer" }} />
                  ) : (
                    <div className={css.playAreaHint}>点击下方发亮的手牌放到这里（{g.items.length} 张可用）</div>
                  )}
                  {card && <div className={css.playAreaCount}>1/1<br /><span>点牌收回</span></div>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className={css.btn} disabled={!staged} onClick={() => staged && onAnswer([staged.id])}>确认</button>
                  <button className={`${css.btn} ${css.btnGhost}`} onClick={() => { setSelected([]); setActive(null); }}>取消</button>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
